import { useRef, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { Text } from "@react-three/drei";
import * as THREE from "three";
import { stressStore, crisisLevel } from "../store";

// A floating spatial control panel that can be driven by XR controller rays
// (hand controllers on Quest / Vision Pro) as well as by mouse on desktop.
// A single invisible interaction plane handles pick + drag for all 3 sliders.

const ROWS = [
  { key: "phd", label: "PhD Production", y: 0.42 },
  { key: "budget", label: "Budget Contraction", y: 0.0 },
  { key: "funding", label: "Funding Cuts", y: -0.42 },
];

const X_MIN = -0.72;
const X_MAX = 0.72;
const TRACK_W = X_MAX - X_MIN;

function valToX(v) {
  return X_MIN + v * TRACK_W;
}

export default function XRControls() {
  const group = useRef();
  const dragging = useRef(null);
  const handleRefs = useRef({});
  const fillRefs = useRef({});
  const tmp = new THREE.Vector3();

  const pickRow = (localY) => {
    let best = ROWS[0];
    let bd = Infinity;
    for (const r of ROWS) {
      const d = Math.abs(localY - r.y);
      if (d < bd) {
        bd = d;
        best = r;
      }
    }
    return best;
  };

  const setFromPoint = (e, rowKey) => {
    // world point -> panel local
    tmp.copy(e.point);
    group.current.worldToLocal(tmp);
    const v = THREE.MathUtils.clamp((tmp.x - X_MIN) / TRACK_W, 0, 1);
    stressStore.setUser({ [rowKey]: v });
  };

  const onDown = (e) => {
    e.stopPropagation();
    tmp.copy(e.point);
    group.current.worldToLocal(tmp);
    const row = pickRow(tmp.y);
    dragging.current = row.key;
    try {
      e.target.setPointerCapture(e.pointerId);
    } catch (err) {}
    setFromPoint(e, row.key);
  };

  const onMove = (e) => {
    if (!dragging.current) return;
    e.stopPropagation();
    setFromPoint(e, dragging.current);
  };

  const onUp = (e) => {
    if (dragging.current) e.stopPropagation();
    dragging.current = null;
    try {
      e.target.releasePointerCapture(e.pointerId);
    } catch (err) {}
  };

  useFrame(() => {
    const s = stressStore.getState();
    ROWS.forEach((r) => {
      const x = valToX(s[r.key]);
      const h = handleRefs.current[r.key];
      if (h) h.position.x = x;
      const f = fillRefs.current[r.key];
      if (f) {
        const w = Math.max(0.001, x - X_MIN);
        f.scale.x = w;
        f.position.x = X_MIN + w / 2;
      }
    });
  });

  return (
    <group ref={group} position={[-3.6, 2.15, 1.4]} rotation={[0, 0.62, 0]}>
      {/* glass-like panel */}
      <mesh position={[0, 0, -0.05]}>
        <planeGeometry args={[2.0, 1.7]} />
        <meshPhysicalMaterial
          color="#ffffff"
          transmission={0.6}
          thickness={0.5}
          roughness={0.25}
          transparent
          opacity={0.55}
          side={THREE.DoubleSide}
        />
      </mesh>
      <Suspense fallback={null}>
        <Text position={[-0.9, 0.72, 0]} anchorX="left" fontSize={0.13} color="#16151a" letterSpacing={0.05}>
          SPATIAL STRESS CONTROLS
        </Text>
      </Suspense>

      {ROWS.map((r) => (
        <group key={r.key} position={[0, r.y, 0]}>
          <Suspense fallback={null}>
            <Text position={[X_MIN, 0.12, 0.01]} anchorX="left" fontSize={0.1} color="#4a4954">
              {r.label}
            </Text>
          </Suspense>
          {/* track */}
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[TRACK_W + 0.08, 0.03, 0.03]} />
            <meshStandardMaterial color="#c9c8c3" />
          </mesh>
          {/* fill */}
          <mesh ref={(el) => (fillRefs.current[r.key] = el)} position={[X_MIN, 0, 0.005]}>
            <boxGeometry args={[1, 0.035, 0.035]} />
            <meshStandardMaterial color="#b3122b" emissive="#ff2d4d" emissiveIntensity={0.4} />
          </mesh>
          {/* handle */}
          <mesh ref={(el) => (handleRefs.current[r.key] = el)} position={[0, 0, 0.02]}>
            <sphereGeometry args={[0.07, 20, 20]} />
            <meshStandardMaterial color="#16151a" metalness={0.5} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* single invisible interaction surface (works for mouse + controller ray) */}
      <mesh
        position={[0, 0, 0.06]}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        <planeGeometry args={[2.0, 1.5]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}
